import * as Yup from 'yup';
import {
  accountNumberRegex,
  AdminAllowanceNames,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  taxRegex,
  TravelAllowanceEnums,
} from '@pharmaplan/common';

import strings from '../localization';

import { onlyNumbersRegex, postalCodeRegExp } from './Constants';
import { formikRequired } from '../components/Admin/SoftwareList/validator';

const { meal, travel, accommodation, emergency, others } = AllowanceEnums;

const {
  travelAllowanceType,
  accommodationAllowanceType,
  mealAllowance,
  travelAllowanceAmount,
  accommodationAllowance,
  paidHourlyRate,
  emergencyFees,
  otherAllowance,
  travelAllowanceRatePerKm,
  weekDayPaidHourlyRate,
  weekendPaidHourlyRate,
} = AllowancesNames;

const { pharmacyHourlyRate } = AdminAllowanceNames;

export const allowanceValidator = () =>
  Yup.number()
    .typeError(strings.mustBeNumber)
    .nullable()
    .max(
      999.99,
      strings.formatString(strings.pleaseEnterLessThan, '1000') as string,
    )
    .moreThan(-1, strings.pleaseEnterValidNumber);

export const numberValidation = Yup.string().matches(
  onlyNumbersRegex,
  strings.numberValidation,
);

export const accountValidation = Yup.string().matches(accountNumberRegex, strings.pleaseEnterValidValue);

export const taxNumberValidation = Yup.string().matches(taxRegex, strings.pleaseEnterValidValue).nullable().max(30, strings.pleaseEnterValidValue);

export const hourlyRateValidator = () =>
  Yup.number()
    .typeError(strings.mustBeNumber)
    .required(strings.requiredField)
    .moreThan(
      0,
      strings.formatString(strings.pleaseEnterMoreThan, '0') as string,
    )
    .max(
      999.99,
      strings.formatString(strings.pleaseEnterLessThan, '1000') as string,
    );

const branchNumberBaseValidation = numberValidation
  .nullable();

const institutionNumberBaseValidation = numberValidation
  .nullable();

const accountNumberBaseValidation = () =>
  accountValidation
    .min(
      4,
      strings.formatString(
        strings.shouldBeFormatNumbers,
        strings.accountNo,
        `${4}`,
      ) as string,
    )
    .nullable();

export const bankAccountValidations = {
  branchNumber: branchNumberBaseValidation.when('isAnyDetailEntered', {
    is: true,
    then: () =>
      branchNumberBaseValidation.required(
        strings.formatString(
          strings.pleaseEnterFormat,
          strings.branchNo,
        ) as string,
      ),
  }),
  institutionNumber: institutionNumberBaseValidation.when(
    'isAnyDetailEntered',
    {
      is: true,
      then: () =>
        institutionNumberBaseValidation.required(
          strings.formatString(
            strings.pleaseEnterFormat,
            strings.institutionNo,
          ) as string,
        ),
    },
  ),
  accountNumber: accountNumberBaseValidation().when('isAnyDetailEntered', {
    is: true,
    then: () =>
      accountNumberBaseValidation().required(
        strings.formatString(
          strings.pleaseEnterFormat,
          strings.accountNo,
        ) as string,
      ),
  }),
};

export const incorporatedProfessionalValidator = () =>
  ({
    corporateCompanyName: Yup.string()
      .nullable()
      .when('incorporated', {
        is: 'true',
        then: () =>
          formikRequired,
      }),
    tps: taxNumberValidation
      .when('incorporated', {
        is: 'true',
        then: () =>
          taxNumberValidation.required(strings.requiredField),
      }),
    tvq: taxNumberValidation
      .when('incorporated', {
        is: 'true',
        then: () =>
          taxNumberValidation.required(strings.requiredField),
      }),
    corporateCity: Yup.string().nullable(),
    corporatePostalCode: Yup.string()
      .nullable()
      .when('incorporated', {
        is: 'true',
        then: () =>
          Yup.string().nullable().matches(postalCodeRegExp, strings.postalCodeValidation),
      }),
    corporatePhone: Yup.string()
      .nullable()
      .when('incorporated', {
        is: 'true',
        then: () =>
          Yup.string().nullable().min(
            12,
          strings.formatString(strings.phoneValidation, strings.phone) as string,
          ),
      }),
  });

export const commonAllowanceValidations = {
  [travelAllowanceType]: Yup.array().when(travel, {
    is: true,
    then: () =>
      Yup.array().min(1, strings.pleaseSelectAValue),
  }),
  [accommodationAllowanceType]: Yup.string().nullable().when(accommodation, {
    is: true,
    then: () =>
      Yup.string().required(strings.requiredField),
  }),
  [mealAllowance]: Yup.number().nullable().when(meal, {
    is: true,
    then: () =>
      allowanceValidator().required(strings.requiredField),
  }),
  [accommodationAllowance]: Yup.number().nullable().when(accommodationAllowanceType, {
    is: AllowanceTypesToHandle.paidValuePerNight,
    then: () =>
      allowanceValidator().required(strings.requiredField),
  }),
  [travelAllowanceRatePerKm]: Yup.number().nullable().when(travelAllowanceType, {
    is: (values: any) =>
      values?.includes(TravelAllowanceEnums.valueKm),
    then: () =>
      allowanceValidator().required(strings.requiredField),
  }),
  [travelAllowanceAmount]: Yup.number().nullable().when(travelAllowanceType, {
    is: (values: any) =>
      values?.includes(TravelAllowanceEnums.travelAmount),
    then: () =>
      Yup.number()
        .typeError(strings.mustBeNumber)
        .required(strings.requiredField)
        .moreThan(
          0,
          strings.formatString(strings.pleaseEnterMoreThan, '0') as string,
        )
        .max(
          999.99,
          strings.formatString(strings.pleaseEnterLessThan, '1000') as string,
        ),
  }),
  [emergencyFees]: Yup.number().nullable().when(emergency, {
    is: true,
    then: allowanceValidator,
  }),
  [otherAllowance]: Yup.number().nullable().when(others, {
    is: true,
    then: allowanceValidator,
  }),
};

export const longTermContractValidation = {
  [weekDayPaidHourlyRate]: Yup.number().nullable().when(['contractType', 'weekdayHours'], {
    is: (contractType: ContractTypes, weekdayHours: number) =>
      contractType === ContractTypes.longTerm && weekdayHours > 0,
    then: hourlyRateValidator,
  }),
  [weekendPaidHourlyRate]: Yup.number().nullable().when(['contractType', 'weekendHours'], {
    is: (contractType: ContractTypes, weekendHours: number) =>
      contractType === ContractTypes.longTerm && weekendHours > 0,
    then: hourlyRateValidator,
  }),
  displayDate: Yup.string().nullable().when('contractType', {
    is: ContractTypes.longTerm,
    then: () =>
      formikRequired,
  }),
};

export const SelfWorkshiftValidator = () =>
  Yup.object().shape({
    ...commonAllowanceValidations,
    ...longTermContractValidation,
    [paidHourlyRate]: Yup.number().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: hourlyRateValidator,
    }),
    datePicker: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
    startTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
    endTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
  });

export const SelfUpdateWorkshiftValidator = () =>
  Yup.object().shape({
    ...commonAllowanceValidations,
    ...longTermContractValidation,
    [paidHourlyRate]: Yup.number().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: hourlyRateValidator,
    }),
    startTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
    endTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
  });

export const AdminWorkshiftValidator = () =>
  Yup.object().shape({
    ...commonAllowanceValidations,
    ...longTermContractValidation,
    [paidHourlyRate]: Yup.number().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: hourlyRateValidator,
    }),
    [pharmacyHourlyRate]: hourlyRateValidator().required(strings.requiredField),
    selectedDate: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(),
    }),
    startTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
    endTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
  });

export const AdminEditWorkshiftValidator = () =>
  Yup.object().shape({
    ...commonAllowanceValidations,
    [paidHourlyRate]: Yup.number().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: hourlyRateValidator,
    }),
    startTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
    endTime: Yup.string().nullable().when('contractType', {
      is: ContractTypes.shortTerm,
      then: () =>
        Yup.string().required(strings.requiredField),
    }),
  });

export const weekRatesValidation = () =>
  ({
    [weekDayPaidHourlyRate]: Yup.number().nullable().when(['weekdays'], {
      is: (weekdays: number) =>
        weekdays > 0,
      then: hourlyRateValidator,
    }),
    [weekendPaidHourlyRate]: Yup.number().nullable().when(['weekends'], {
      is: (weekends: number) =>
        weekends > 0,
      then: hourlyRateValidator,
    }),
  });
